import React, { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import useThemeColor from "../../hooks/useThemeColor";
import useDarkMode from "../../hooks/useDarkMode";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast";
import { Panel, PanelBody, PanelHeader } from "../../components/panel/panel";
import * as XLSX from "xlsx";
import sheetBlueprint from "./bulkUploadBlueprint";

const normalizeErrorList = (payloadErrors = []) => {
  if (!Array.isArray(payloadErrors)) return [];
  return payloadErrors.map((err, index) => ({
    id: err.id || `${err.sheet || "Sheet"}-${err.row || index}`,
    sheet: err.sheet || err.Sheet || "Unknown",
    row: err.row || err.Row || "-",
    field: err.field || err.Field || "-",
    message: err.message || err.Message || "Unknown error",
  }));
};

const BulkUploadProducts = () => {
  const navigate = useNavigate();
  const [selectedFile, setSelectedFile] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [errors, setErrors] = useState([]);
  const [previewSheets, setPreviewSheets] = useState([]);
  const [previewError, setPreviewError] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const fileInputRef = useRef(null);
  const themeColor = useThemeColor();
  const isDarkMode = useDarkMode();

  // Helper function to format cell values for display
  const formatCellValue = (value) => {
    // Handle null, undefined, or empty string
    if (value === null || value === undefined || value === '') {
      return '';
    }
    
    // Handle boolean values - convert to TRUE/FALSE
    if (typeof value === 'boolean') {
      return value ? 'TRUE' : 'FALSE';
    }
    
    // Handle string "true"/"false" (case-insensitive)
    if (typeof value === 'string') {
      const lowerValue = value.trim().toLowerCase();
      if (lowerValue === 'true') return 'TRUE';
      if (lowerValue === 'false') return 'FALSE';
    }
    
    // Return the value as-is for other types (numbers, strings, etc.)
    return String(value);
  };

  const summary = useMemo(() => {
    if (!uploadResult) {
      return {
        total: 0,
        success: 0,
        errors: 0,
      };
    }
    const total =
      uploadResult.totalProcessed ??
      uploadResult.total ??
      uploadResult.totalCount ??
      0;
    const success =
      uploadResult.successCount ??
      uploadResult.success ??
      uploadResult.processed ??
      0;
    const errorCount =
      uploadResult.errorCount ?? uploadResult.errors?.length ?? errors.length;
    return { total, success, errors: errorCount };
  }, [uploadResult, errors]);

  const validateTemplate = (workbook) => {
    const validationErrors = [];
    
    // Check if workbook has any sheets at all
    if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
      validationErrors.push(
        "This file does not appear to be a valid Excel template. The file format does not match the expected template structure."
      );
      return {
        isValid: false,
        errors: validationErrors,
        warnings: [],
      };
    }
    
    // Normalize sheet names for comparison (trim and case-insensitive)
    const normalizeSheetName = (name) => String(name).trim();
    const foundSheets = new Set(
      workbook.SheetNames.map((name) => normalizeSheetName(name))
    );
    
    // Check for required sheets (excluding Product_Variants as it's optional)
    const requiredSheets = sheetBlueprint
      .filter(
        (sheet) =>
          sheet.type === "Editable" && sheet.name !== "Product_Variants"
      )
      .map((sheet) => sheet.name);
    
    const missingSheets = requiredSheets.filter(
      (sheetName) => !foundSheets.has(normalizeSheetName(sheetName))
    );
    
    if (missingSheets.length > 0) {
      validationErrors.push(
        `File format does not match the expected template. Missing required sheets: ${missingSheets.join(", ")}. Please download the latest template and ensure all sheets are present.`
      );
    }
    
    // Check if file has too few sheets (might be wrong template)
    // Expected: 7 sheets total (2 reference + 5 editable, with Product_Variants being optional)
    const minExpectedSheets = 6; // At least 2 reference + 4 required editable
    if (workbook.SheetNames.length < minExpectedSheets) {
      validationErrors.push(
        `File format does not match. Expected at least ${minExpectedSheets} sheets but found ${workbook.SheetNames.length}. This file may be from an older template version or is missing required sheets.`
      );
    }
    
    return {
      isValid: validationErrors.length === 0,
      errors: validationErrors,
      warnings: [],
    };
  };

  const parsePreview = async (file) => {
    if (!file) {
      setPreviewSheets([]);
      setValidationResult(null);
      return;
    }
    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array" });
      
      // Validate template structure
      const validation = validateTemplate(workbook);
      setValidationResult(validation);
      
      if (!validation.isValid) {
        setPreviewSheets([]);
        setPreviewError(""); // Clear any previous preview errors
        // Show toast notification for validation failure
        showErrorToast(
          `Template validation failed: ${validation.errors[0] || "File format does not match"}`
        );
        return;
      }
      
      setPreviewError(""); // Clear errors if validation passes
      
      const readOnlySheets = ["Category_Type", "Category_Departments"];
      const sheets = workbook.SheetNames.filter(
        (sheetName) => !readOnlySheets.includes(sheetName)
      ).map((sheetName) => {
        const worksheet = workbook.Sheets[sheetName];
        const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        const headers = rows[0] || [];
        const dataRows = rows.slice(1, 6);
        return {
          name: sheetName,
          headers,
          rows: dataRows,
          totalRows: Math.max(rows.length - 1, 0),
        };
      });
      setPreviewSheets(sheets);
      setPreviewError("");
    } catch (err) {
      console.error("Failed to parse workbook preview", err);
      setPreviewSheets([]);
      setValidationResult({
        isValid: false,
        errors: ["Unable to parse the file. Please ensure it is a valid .xlsx template."],
        warnings: [],
      });
      setPreviewError(
        "Unable to preview this file. Please ensure it is a valid .xlsx template."
      );
    }
  };

  const handleFileChange = async (eventOrFile) => {
    const file = eventOrFile?.target
      ? eventOrFile.target.files?.[0]
      : eventOrFile;
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".xlsx")) {
      showErrorToast("Only .xlsx files are supported.");
      return;
    }
    setSelectedFile(file);
    setUploadResult(null);
    setErrors([]);
    await parsePreview(file);
  };

  const handleDrop = async (event) => {
    event.preventDefault();
    setDragActive(false);
    const file = event.dataTransfer.files?.[0];
    if (file) {
      await handleFileChange(file);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    if (!dragActive) setDragActive(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    if (dragActive) setDragActive(false);
  };

  const handleDownloadTemplate = async () => {
    try {
      setDownloading(true);
      const response = await axiosInstance.get(
        APIs.GET_BULK_UPLOAD_TEMPLATE,
        {
          responseType: "blob",
        }
      );

      // Check if response is actually a blob (not JSON error wrapped)
      if (response.data instanceof Blob) {
        // Verify it's not a JSON error by checking size and type
        if (response.data.size < 100 && response.data.type === "application/json") {
          // Likely an error response wrapped as blob
          const text = await response.data.text();
          try {
            const errorData = JSON.parse(text);
            showErrorToast(errorData.message || "Failed to download template.");
            return;
          } catch {
            // Not JSON, continue
          }
        }
      }

      // Ensure we have valid data
      if (!response.data || (response.data instanceof Blob && response.data.size === 0)) {
        showErrorToast("Downloaded file is empty. Please try again.");
        return;
      }

      const blob = response.data instanceof Blob 
        ? response.data 
        : new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });

      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "BulkUploadTemplate.xlsx";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
      showSuccessToast("Template downloaded successfully.");
    } catch (error) {
      console.error("Download error:", error);
      let message = "Failed to download template.";
      
      if (error.response) {
        // If response is JSON, try to parse it
        if (error.response.data instanceof Blob) {
          try {
            const text = await error.response.data.text();
            const errorData = JSON.parse(text);
            message = errorData.message || message;
          } catch {
            message = error.response.statusText || message;
          }
        } else if (error.response.data?.message) {
          message = error.response.data.message;
        }
      } else if (error.message) {
        message = error.message;
      }
      
      showErrorToast(message);
    } finally {
      setDownloading(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      showErrorToast("Please select an .xlsx file first.");
      return;
    }

    if (!selectedFile.name.toLowerCase().endsWith(".xlsx")) {
      showErrorToast("Only .xlsx files are supported.");
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      setUploading(true);
      setUploadResult(null);
      setErrors([]);

      const response = await axiosInstance.post(
        APIs.POST_BULK_UPLOAD_PROCESS,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      const payload = response.data?.data || response.data;
      const normalizedErrors = normalizeErrorList(
        payload?.errors || response.data?.errors
      );

      setUploadResult(payload);
      setErrors(normalizedErrors);
      showSuccessToast(
        response.data?.message || "File processed. Review the results below."
      );
    } catch (error) {
      const fallbackErrors = normalizeErrorList([
        {
          sheet: "N/A",
          row: "-",
          field: "-",
          message:
            error.response?.data?.message ||
            "Unable to process the uploaded file.",
        },
      ]);
      setErrors(fallbackErrors);
      showErrorToast(
        error.response?.data?.message || "Failed to process the uploaded file."
      );
    } finally {
      setUploading(false);
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setUploadResult(null);
    setErrors([]);
    setPreviewSheets([]);
    setPreviewError("");
    setValidationResult(null);
  };

  const handleExportErrors = () => {
    if (!errors.length) {
      showErrorToast("There are no errors to export.");
      return;
    }
    const header = "Sheet,Row,Field,Message";
    const rows = errors.map(
      (err) => `${err.sheet},${err.row},${err.field},"${err.message}"`
    );
    const csvContent = [header, ...rows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "bulk-upload-errors.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="container-fluid py-3">
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-3">
        <div>
          <h2 className="mb-1">Bulk Upload – Products & Structure</h2>
          <p className="text-muted mb-0">
            Download the Excel template, fill the sheets in order, and upload to
            import categories, modifier groups, modifiers, products, and
            variants.
          </p>
        </div>
        <div className="d-flex gap-2 flex-wrap">
            <button
              className="btn btn-outline-info"
              onClick={() => navigate("/items/bulkUploadGuide")}
            >
              <i className="fa fa-book me-2" />
              View Template Guide
            </button>
          <button
            className="btn btn-outline-secondary"
            onClick={handleReset}
            disabled={!selectedFile && !uploadResult && !errors.length}
          >
            Reset
          </button>
          <button
            className="btn btn-primary"
            onClick={handleDownloadTemplate}
            disabled={downloading}
          >
            {downloading ? (
              <>
                <i className="fa fa-spinner fa-spin me-2" />
                Preparing Template...
              </>
            ) : (
              <>
                <i className="fa fa-download me-2" />
                Download Template
              </>
            )}
          </button>
        </div>
      </div>

      <Panel className="mb-4">
        <PanelHeader>Upload Workbook</PanelHeader>
        <PanelBody>
          <div className="row g-3">
            <div className="col-12">
              <label className="form-label fw-semibold">
                Select filled template (.xlsx)
              </label>
              <div
                className={`d-flex flex-column align-items-center justify-content-center text-center p-4 border rounded ${
                  dragActive ? "shadow-sm" : ""
                }`}
                style={{
                  borderStyle: "dashed",
                  borderColor: dragActive ? themeColor : "#ced4da",
                  backgroundColor: isDarkMode ? "#2d3436" : "#fdfdfd",
                  minHeight: "170px",
                  cursor: "pointer",
                  transition: "border-color 0.2s ease",
                }}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onDragLeave={handleDragLeave}
                onClick={() => fileInputRef.current?.click()}
              >
                <i
                  className="fa fa-cloud-upload fa-2x mb-3"
                  style={{ color: themeColor }}
                />
                <p className="mb-1">
                  Drag &amp; drop your completed workbook here, or click to
                  browse.
                </p>
                <small className="text-muted">
                  Only .xlsx files generated from the latest template are
                  supported.
                </small>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="d-none"
                  accept=".xlsx"
                  onChange={handleFileChange}
                />
              </div>
              {selectedFile && (
                <div className="mt-2 text-muted small">
                  Selected: {selectedFile.name}
                </div>
              )}
              {validationResult && (
                <div className="mt-3">
                  {validationResult.errors.length > 0 && (
                    <div className="alert alert-danger mb-2 py-3" style={{ borderLeft: '4px solid #dc3545' }}>
                      <strong className="d-block mb-2">
                        <i className="fa fa-exclamation-circle me-2" />
                        File Format Does Not Match Template
                      </strong>
                      <div className="mb-2">
                        {validationResult.errors.map((error, idx) => (
                          <div key={idx} className="mb-1">{error}</div>
                        ))}
                      </div>
                      <div className="mt-2 pt-2 border-top">
                        <small>
                          <i className="fa fa-info-circle me-1" />
                          <strong>Solution:</strong> Please download the latest template using the "Download Template" button above, fill it with your data, and try uploading again.
                        </small>
                      </div>
                    </div>
                  )}
                  {validationResult.isValid && (
                      <div className="alert alert-success mb-2 py-2">
                        <i className="fa fa-check-circle me-2" />
                        <strong>Template validated successfully!</strong> The file
                        structure matches the expected template format.
                      </div>
                    )}
                </div>
              )}
              {previewError && !validationResult && (
                <div className="alert alert-warning mt-2 mb-0 py-2">
                  {previewError}
                </div>
              )}
            </div>
            <div className="col-12 d-flex gap-2 flex-wrap">
              <button
                className="btn btn-success"
                onClick={handleUpload}
                disabled={
                  uploading ||
                  !selectedFile ||
                  (validationResult && !validationResult.isValid)
                }
                title={
                  validationResult && !validationResult.isValid
                    ? "Please fix template validation errors before uploading"
                    : ""
                }
              >
                {uploading ? (
                  <>
                    <i className="fa fa-spinner fa-spin me-2" />
                    Uploading...
                  </>
                ) : validationResult && !validationResult.isValid ? (
                  <>
                    <i className="fa fa-exclamation-triangle me-2" />
                    Fix Template Errors
                  </>
                ) : (
                  <>
                    <i className="fa fa-upload me-2" />
                    Upload & Process
                  </>
                )}
              </button>
              <button
                className="btn btn-outline-danger"
                onClick={handleExportErrors}
                disabled={!errors.length}
              >
                <i className="fa fa-file-excel me-2" />
                Export Errors
              </button>
            </div>
          </div>
        </PanelBody>
      </Panel>

      {!!previewSheets.length && (
        <Panel className="mb-4">
          <PanelHeader>Workbook Preview</PanelHeader>
          <PanelBody>
            <div className="row g-3">
              {previewSheets.map((sheet) => (
                <div className="col-12" key={sheet.name}>
                  <div className="border rounded h-100">
                    <div
                      className="px-3 py-2 d-flex justify-content-between align-items-center"
                      style={{
                        backgroundColor: isDarkMode ? "#2f3640" : "#f5f6fa",
                        borderBottom: "1px solid #dee2e6",
                      }}
                    >
                      <div>
                        <strong>{sheet.name}</strong>
                        <div className="small text-muted">
                          Showing {sheet.rows.length} of {sheet.totalRows} rows
                        </div>
                      </div>
                      <span className="badge bg-secondary">
                        {sheet.headers.length} columns
                      </span>
                    </div>
                    <div className="table-responsive">
                      <table className="table table-sm mb-0">
                        <thead className="table-light">
                          <tr>
                            {sheet.headers.map((header, idx) => (
                              <th key={`${sheet.name}-head-${idx}`}>
                                {header || `Column ${idx + 1}`}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {sheet.rows.length ? (
                            sheet.rows.map((row, rowIdx) => (
                              <tr key={`${sheet.name}-row-${rowIdx}`}>
                                {sheet.headers.map((_, colIdx) => (
                                  <td key={`${sheet.name}-col-${colIdx}`}>
                                    {formatCellValue(row[colIdx])}
                                  </td>
                                ))}
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={sheet.headers.length || 1}>
                                <em>No data rows detected.</em>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </PanelBody>
        </Panel>
      )}

      {(uploading || uploadResult || errors.length > 0) && (
        <Panel>
          <PanelHeader>
            Results & Diagnostics
            {uploading && (
              <span className="ms-2">
                <i className="fa fa-spinner fa-spin text-primary"></i>
                <span className="ms-2 text-muted">Processing...</span>
              </span>
            )}
          </PanelHeader>
          <PanelBody>
            {uploading ? (
              <div className="text-center py-5">
                <div className="mb-3">
                  <i className="fa fa-spinner fa-spin fa-3x text-primary"></i>
                </div>
                <h5>Processing your upload...</h5>
                <p className="text-muted">
                  Please wait while we process your file. This may take a few moments.
                </p>
                <div className="progress mt-4" style={{ height: "6px" }}>
                  <div
                    className="progress-bar progress-bar-striped progress-bar-animated"
                    role="progressbar"
                    style={{ width: "100%" }}
                  ></div>
                </div>
              </div>
            ) : (
              <>
                <div className="row g-3 mb-4">
                  <div className="col-md-4">
                    <div className="p-3 border rounded text-center">
                      <div className="text-muted small">Total Rows Processed</div>
                      <div className="fs-3 fw-bold">{summary.total}</div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="p-3 border rounded text-center">
                      <div className="text-muted small">Successful</div>
                      <div className="fs-3 fw-bold text-success">
                        {summary.success}
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="p-3 border rounded text-center">
                      <div className="text-muted small">Errors</div>
                      <div className="fs-3 fw-bold text-danger">
                        {summary.errors}
                      </div>
                    </div>
                  </div>
                </div>

                {uploadResult?.processedCounts && (
                  <div className="mb-4">
                    <h6 className="mb-3">Processing Summary by Sheet:</h6>
                    <div className="row g-2">
                      {Object.entries(uploadResult.processedCounts).map(([sheet, count]) => (
                        <div key={sheet} className="col-md-3">
                          <div className="p-2 border rounded bg-light">
                            <div className="small text-muted">{sheet}</div>
                            <div className="fw-bold">{count} processed</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {!!errors.length && (
                  <div className="table-responsive">
                    <table className="table table-striped table-bordered">
                      <thead
                        style={{
                          backgroundColor: isDarkMode ? "#353b48" : themeColor,
                          color: isDarkMode ? "#f5f6fa" : "#fff",
                        }}
                      >
                        <tr>
                          <th>#</th>
                          <th>Sheet</th>
                          <th>Row</th>
                          <th>Field</th>
                          <th>Error Message</th>
                        </tr>
                      </thead>
                      <tbody>
                        {errors.map((error, index) => (
                          <tr key={error.id || index}>
                            <td>{index + 1}</td>
                            <td>{error.sheet}</td>
                            <td>{error.row}</td>
                            <td>{error.field}</td>
                            <td>{error.message}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {!errors.length && uploadResult && (
                  <div className="alert alert-success">
                    <i className="fa fa-check-circle me-2"></i>
                    <strong>Upload completed successfully!</strong> All data has been processed without errors.
                  </div>
                )}
              </>
            )}
          </PanelBody>
        </Panel>
      )}
    </div>
  );
};

export default BulkUploadProducts;

